import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Link,
  alpha,
  Tabs,
  Tab
} from '@mui/material';
import { FavoriteBorder } from '@mui/icons-material';
import AuthContext from '../context/AuthContext';
import SEO from '../components/SEO';
import { trackSignup, trackLogin } from '../utils/analytics';

const RegistrationPage = () => {
  const { backendUrl, loginUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState(0); // 0 = Sign Up, 1 = Sign In

  // Registration form state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  // Bot protection state
  const [formLoadTime] = useState(Date.now());
  const [mathQuestion, setMathQuestion] = useState({ num1: 0, num2: 0, id: '' });
  const [mathAnswer, setMathAnswer] = useState('');

  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showLoginButton, setShowLoginButton] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const navigate = useNavigate();

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSnackbarOpen(false);
    setShowLoginButton(false);
    setEmailNotVerified(false);
    setPasswordError('');
    setConfirmPasswordError('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });

    // Clear errors when user types
    if (e.target.name === 'password') {
      setPasswordError('');
    }
    if (e.target.name === 'confirmPassword') {
      setConfirmPasswordError('');
    }
  };

  const handleLoginChange = (e) => {
    setLoginData({
      ...loginData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setShowLoginButton(false);
    setPasswordError('');
    setConfirmPasswordError('');

    // Validate password length
    if (formData.password.length < 6) {
      setPasswordError('Password must be at least 6 characters long');
      setSnackbarMessage('Password must be at least 6 characters long');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      setSnackbarMessage('Passwords do not match');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    // Validate math answer
    if (!mathAnswer || parseInt(mathAnswer) !== (mathQuestion.num1 + mathQuestion.num2)) {
      setSnackbarMessage('Please answer the verification question correctly');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${backendUrl}api/auth/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          website: '', // Honeypot field (should remain empty)
          formLoadTime: formLoadTime,
          mathAnswer: mathAnswer,
          mathQuestionId: mathQuestion.id
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (errorData.Failed && errorData.Failed.toLowerCase().includes('user exists')) {
          setShowLoginButton(true);
          throw new Error('An account with this email already exists.');
        } else {
          throw new Error(errorData.message || 'Registration failed');
        }
      }

      // Track successful registration
      trackSignup('email');
      navigate(`/verify-email?email=${encodeURIComponent(formData.email)}`);
    } catch (error) {
      setSnackbarMessage(error.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailNotVerified(false);
    setSnackbarOpen(false);

    try {
      await loginUser(loginData.email, loginData.password);
      // Track successful login
      trackLogin('email');
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('email not verified')) {
        setEmailNotVerified(true);
        setSnackbarMessage("Your email must be verified to login. Please check your email for the verification code.");
      } else if (err.message === "No active account found with the given credentials") {
        setSnackbarMessage("Invalid email or password. Please try again.");
      } else if (err.message.includes("NetworkError")) {
        setSnackbarMessage("Network error. Please check your connection.");
      } else {
        setSnackbarMessage(err.message || "An unexpected error occurred. Please try again later.");
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setSnackbarOpen(false);

    try {
      const response = await fetch(`${backendUrl}api/auth/resend-email-code/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: loginData.email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to send verification code');
      }

      setSnackbarMessage('Verification code sent! Please check your inbox.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (err) {
      setSnackbarMessage(err.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setResendLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSwitchToLogin = () => {
    setActiveTab(1);
  };

  // Generate random math question when component mounts or tab changes
  useEffect(() => {
    if (activeTab === 0) {
      const num1 = Math.floor(Math.random() * 10) + 1;
      const num2 = Math.floor(Math.random() * 10) + 1;
      setMathQuestion({ num1, num2, id: `${num1}_${num2}` });
      setMathAnswer('');
    }
  }, [activeTab]);

  return (
    <>
      <SEO
        title="Sign Up - Couplendar | Start Tracking Relationship Balance"
        description="Create your free Couplendar account and start tracking chores, time-off, and contributions. Build a more equal and balanced relationship."
        keywords="sign up couple app, register chore tracker, relationship balance tracker, couple accountability"
        url="https://couplendar.com/register"
      />
      <Box sx={{
        minHeight: '100vh',
        backgroundColor: '#F9F7F4',
        py: { xs: 4, md: 8 }
      }}>
      <Container maxWidth="sm">
        {/* Logo */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            mb: 4,
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        >
          <FavoriteBorder sx={{ fontSize: 40, color: '#C4856A' }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: '#7B6B8D',
            }}
          >
            Couplendar
          </Typography>
        </Box>

        <Card
          sx={{
            background: 'white',
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            borderRadius: 3,
            border: 'none'
          }}
        >
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {registrationSuccess ? (
              <Box textAlign="center">
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: '#7B6B8D',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3
                  }}
                >
                  <FavoriteBorder sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    color: '#333',
                    mb: 2
                  }}
                >
                  Welcome!
                </Typography>
                <Typography variant="body1" sx={{ mb: 4, color: '#6B6B6B' }}>
                  Registration successful! Please check your email for a 5-digit verification code.
                  The code is valid for 5 minutes.
                </Typography>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    setRegistrationSuccess(false);
                    setActiveTab(1);
                  }}
                  sx={{
                    borderRadius: 2,
                    py: 1.5,
                    backgroundColor: '#C4856A',
                    boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                    '&:hover': {
                      backgroundColor: '#B0745A',
                      boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                    }
                  }}
                >
                  Go to Sign In
                </Button>
              </Box>
            ) : (
              <>
                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="fullWidth"
                    sx={{
                      '& .MuiTab-root': {
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        color: '#9B9B9B',
                        '&.Mui-selected': {
                          color: '#7B6B8D'
                        }
                      },
                      '& .MuiTabs-indicator': {
                        backgroundColor: '#7B6B8D',
                        height: 3,
                        borderRadius: '3px 3px 0 0'
                      }
                    }}
                  >
                    <Tab label="Sign Up" />
                    <Tab label="Sign In" />
                  </Tabs>
                </Box>

                {/* Sign Up Form */}
                {activeTab === 0 && (
                  <Box component="form" onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        inputProps={{ maxLength: 254 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        required
                        error={!!passwordError}
                        helperText={passwordError || "Minimum 6 characters"}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        required
                        error={!!confirmPasswordError}
                        helperText={confirmPasswordError || ""}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="First Name"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        inputProps={{ maxLength: 50 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Last Name"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        inputProps={{ maxLength: 50 }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />

                      {/* Bot protection: Math question */}
                      <Box sx={{
                        p: 2,
                        backgroundColor: alpha('#7B6B8D', 0.05),
                        borderRadius: 2,
                        border: `1px solid ${alpha('#7B6B8D', 0.2)}`
                      }}>
                        <Typography variant="body2" sx={{ mb: 1, color: '#666', fontWeight: 500 }}>
                          Please verify you're human:
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1.5, fontWeight: 600, color: '#333' }}>
                          What is {mathQuestion.num1} + {mathQuestion.num2}?
                        </Typography>
                        <TextField
                          fullWidth
                          type="number"
                          value={mathAnswer}
                          onChange={(e) => setMathAnswer(e.target.value)}
                          placeholder="Your answer"
                          required
                          sx={{
                            backgroundColor: 'white',
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              '&.Mui-focused fieldset': {
                                borderColor: '#7B6B8D'
                              }
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                              color: '#7B6B8D'
                            }
                          }}
                        />
                      </Box>

                      {/* Honeypot field - hidden from users */}
                      <input
                        type="text"
                        name="website"
                        tabIndex="-1"
                        autoComplete="off"
                        style={{
                          position: 'absolute',
                          left: '-9999px',
                          width: '1px',
                          height: '1px',
                          opacity: 0
                        }}
                      />

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          mt: 1,
                          backgroundColor: '#C4856A',
                          boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                          '&:hover': {
                            backgroundColor: '#B0745A',
                            boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Account'}
                      </Button>

                      {showLoginButton && (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleSwitchToLogin}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            borderColor: '#7B6B8D',
                            color: '#7B6B8D',
                            '&:hover': {
                              borderColor: '#6A5A7A',
                              background: alpha('#7B6B8D', 0.04)
                            }
                          }}
                        >
                          Sign In Instead
                        </Button>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* Sign In Form */}
                {activeTab === 1 && (
                  <Box component="form" onSubmit={handleLogin}>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={loginData.email}
                        onChange={handleLoginChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Password"
                        name="password"
                        type="password"
                        value={loginData.password}
                        onChange={handleLoginChange}
                        required
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&.Mui-focused fieldset': {
                              borderColor: '#7B6B8D'
                            }
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: '#7B6B8D'
                          }
                        }}
                      />

                      {emailNotVerified && (
                        <Button
                          fullWidth
                          variant="outlined"
                          onClick={handleResendVerification}
                          disabled={resendLoading}
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            borderColor: '#7B6B8D',
                            color: '#7B6B8D',
                            '&:hover': {
                              borderColor: '#6A5A7A',
                              background: alpha('#7B6B8D', 0.04)
                            }
                          }}
                        >
                          {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                        </Button>
                      )}

                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        disabled={loading}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          mt: 1,
                          backgroundColor: '#C4856A',
                          boxShadow: '0 4px 12px rgba(196, 133, 106, 0.3)',
                          '&:hover': {
                            backgroundColor: '#B0745A',
                            boxShadow: '0 6px 16px rgba(196, 133, 106, 0.4)'
                          }
                        }}
                      >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
                      </Button>
                    </Stack>

                    <Box textAlign="center" mt={3}>
                      <Typography variant="body2" sx={{ color: '#9B9B9B' }}>
                        <Link
                          href="/forgot-password"
                          sx={{
                            color: '#7B6B8D',
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': {
                              textDecoration: 'underline'
                            }
                          }}
                        >
                          Forgot password?
                        </Link>
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
            <Link href="/privacy-policy" sx={{ color: '#7B6B8D', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Privacy Policy
            </Link>
            {' · '}
            <Link href="/terms-of-service" sx={{ color: '#7B6B8D', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Terms of Service
            </Link>
          </Typography>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: snackbarSeverity === 'error' ? '#C4856A' : '#A8D5BA'
              }
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
    </>
  );
};

export default RegistrationPage;
